import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  clearProductRate,
  describeStructure,
  formatUnits,
  priceFor,
  productRatesQuery,
  rateStructuresQuery,
  setProductRate,
  type ProductRate,
  type RateStructure,
} from '../api/pricing.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';

// A product's hire rate in each price group (CB-REQ PRC-02, PRC-03 at v1.942), and a price check:
// what a hire of a given length costs at each rate, from the same function a quote will use. That
// table is what the office compares against Booqable before trusting AVenue's figures (CUT-01).
//
// Rendered only for someone above the price line; the API refuses everyone else regardless.

const CHECK_LENGTHS: Record<string, number[]> = {
  day: [1, 2, 3, 7, 8, 14, 28, 35],
  hour: [2, 4, 6, 8, 10, 12],
  km: [10, 50, 100, 250],
};

export function RatesSection({ productId, mayManage }: { productId: string; mayManage: boolean }) {
  const rates = useQuery(productRatesQuery(productId));
  const structures = useQuery(rateStructuresQuery);
  const [editing, setEditing] = useState<string | null>(null);

  const byId = new Map((structures.data ?? []).map((s) => [s.id, s]));

  return (
    <section className="flex flex-col gap-4" aria-labelledby="rates">
      <h2 id="rates" className="section-title">
        Hire rates
      </h2>
      {rates.isError ? (
        <p role="alert" className="alert">
          {rates.error.message}
        </p>
      ) : null}
      {rates.data ? (
        <div className="overflow-x-auto">
          <table className="data-table">
            <caption className="sr-only">Hire rate in each price group, ex GST</caption>
            <thead>
              <tr>
                <th scope="col">Price group</th>
                <th scope="col">Rate, ex GST</th>
                <th scope="col">Structure</th>
                {mayManage ? (
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rates.data.map((r) => (
                <tr key={r.priceGroup.id}>
                  <th scope="row" className="font-semibold">
                    {r.priceGroup.name}
                    {r.priceGroup.isDefault ? <span className="tag ml-2">default</span> : null}
                  </th>
                  <td className="tabular-nums">
                    {r.rate ? (
                      <>
                        {formatMinorUnits(r.rate.amount.amountMinor, r.rate.amount.currency)}
                        <span className="text-muted"> per unit</span>
                        {r.inherited ? (
                          <span className="block text-xs text-muted">
                            from the default group — no {r.priceGroup.name.toLowerCase()} rate of
                            its own
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted">No rate yet</span>
                    )}
                  </td>
                  <td className="text-sm">{r.rate?.rateStructure.name ?? '—'}</td>
                  {mayManage ? (
                    <td className="text-right">
                      <button
                        type="button"
                        className="link cursor-pointer text-sm"
                        onClick={() => setEditing(r.priceGroup.id)}
                      >
                        {r.rate && !r.inherited ? 'Edit' : 'Set'}
                        <span className="sr-only"> the {r.priceGroup.name} rate</span>
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {editing && rates.data && structures.data ? (
        <RateForm
          key={editing}
          productId={productId}
          current={rates.data.find((r) => r.priceGroup.id === editing)!}
          structures={structures.data}
          onDone={() => setEditing(null)}
        />
      ) : null}

      {rates.data && structures.data ? <PriceCheck rates={rates.data} structures={byId} /> : null}
    </section>
  );
}

function RateForm({
  productId,
  current,
  structures,
  onDone,
}: {
  productId: string;
  current: ProductRate;
  structures: RateStructure[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const own = current.rate && !current.inherited ? current.rate : null;
  const [amount, setAmount] = useState(own ? minorUnitsToDollars(own.amount.amountMinor) : '');
  const [structureId, setStructureId] = useState(
    own?.rateStructure.id ?? current.rate?.rateStructure.id ?? structures[0]?.id ?? '',
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: productRatesQuery(productId).queryKey });
  const save = useMutation({
    mutationFn: (amountMinor: string) =>
      setProductRate(productId, current.priceGroup.id, {
        amountMinor,
        rateStructureId: structureId,
      }),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });
  const clear = useMutation({
    mutationFn: () => clearProductRate(productId, current.priceGroup.id),
    onSuccess: async () => {
      await refresh();
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setLocalError(null);
    const minor = dollarsToMinorUnits(amount);
    if (minor === null) {
      setLocalError('The rate should be an amount in dollars, like 45 or 45.50.');
      return;
    }
    save.mutate(minor);
  }

  const chosen = structures.find((s) => s.id === structureId);
  const failure = save.isError ? save.error : clear.isError ? clear.error : null;
  const error = localError ?? failure?.message ?? null;
  const busy = save.isPending || clear.isPending;

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h3 className="section-title">{current.priceGroup.name} rate</h3>
      {error ? (
        <p role="alert" className="alert">
          {error}
        </p>
      ) : null}
      <Field
        label="Rate per chargeable unit, AUD, ex GST"
        inputMode="decimal"
        required
        value={amount}
        onChange={(e) => setAmount(e.currentTarget.value)}
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="rate-structure" className="text-xs text-ink-soft">
          Rate structure
        </label>
        <select
          id="rate-structure"
          value={structureId}
          onChange={(e) => setStructureId(e.currentTarget.value)}
          className="input"
        >
          {structures.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        {chosen ? <p className="text-xs text-muted">{describeStructure(chosen)}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
        {/* A group’s own rate can be removed so it falls back to the default; the default’s own
            rate can be removed too, which leaves the product unpriced until one is set. */}
        {own ? (
          <Button
            type="button"
            variant="quiet"
            className="ml-auto"
            onClick={() => clear.mutate()}
            disabled={busy}
          >
            {clear.isPending
              ? 'Removing…'
              : current.priceGroup.isDefault
                ? 'Remove this rate'
                : 'Use the default rate instead'}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function PriceCheck({
  rates,
  structures,
}: {
  rates: ProductRate[];
  structures: Map<string, RateStructure>;
}) {
  const priced = rates.filter((r) => r.rate && structures.has(r.rate.rateStructure.id));
  if (priced.length === 0) return null;
  const first = structures.get(priced[0]!.rate!.rateStructure.id)!;
  const lengths = first.kind === 'fixed' ? [1] : (CHECK_LENGTHS[first.unit ?? 'day'] ?? [1]);
  const unit = first.unit ?? 'hire';

  return (
    <div className="flex flex-col gap-2">
      <h3 className="kicker">Price check — ex GST, before discounts</h3>
      <div className="overflow-x-auto">
        <table className="data-table">
          <caption className="sr-only">
            What a hire of each length costs in each price group
          </caption>
          <thead>
            <tr>
              <th scope="col">
                {first.kind === 'fixed' ? 'Hire' : `${unit[0]!.toUpperCase()}${unit.slice(1)}s`}
              </th>
              {priced.map((r) => (
                <th key={r.priceGroup.id} scope="col" className="text-right">
                  {r.priceGroup.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lengths.map((n) => (
              <tr key={n}>
                <th scope="row" className="tabular-nums">
                  {first.kind === 'fixed' ? 'Any length' : n}
                </th>
                {priced.map((r) => {
                  const structure = structures.get(r.rate!.rateStructure.id)!;
                  const { units, total } = priceFor(r.rate!.amount, structure, n);
                  return (
                    <td key={r.priceGroup.id} className="text-right tabular-nums">
                      {formatMinorUnits(total.minor.toString(), total.currency)}
                      <span className="block text-xs text-muted">
                        {formatUnits(units)} {formatUnits(units) === '1' ? 'unit' : 'units'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
